/* @jsx h */
import { defineComponent, h, PropType } from "vue";
import { RouterLink } from "vue-router";
import type { EventWithShop } from "@lemmonhq/types";
import { useLocalization } from "@lemmonhq/uikit";

const PLACEHOLDER_IMAGE =
  "https://placehold.co/400x300/f3f4f6/9ca3af?text=Event";

export const EventsSection = defineComponent({
  props: {
    events: {
      type: Array as PropType<EventWithShop[]>,
      required: true,
    },
    loading: {
      type: Boolean,
      required: true,
    },
    eventsUrl: {
      type: String,
      required: true,
    },
    shopSlug: {
      type: String,
      required: true,
    },
  },
  setup(props) {
    const { locale, t } = useLocalization();
    const formatDate = (dateString: string) => {
      const date = new Date(dateString);
      return date.toLocaleDateString(locale.value || "en", {
        weekday: "short",
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    };

    return () => (
      <section>
        <div class="flex items-center justify-between mb-6">
          <h2 class="text-2xl font-serif font-light text-gray-900">
            {t("customer.home.events")}
          </h2>
          <RouterLink
            to={props.eventsUrl}
            class="text-sm text-gray-600 hover:text-gray-900 font-light"
          >
            {t("customer.restaurant_profile.view_all_events")} →
          </RouterLink>
        </div>

        {props.loading ? (
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            {Array.from({ length: 2 }).map((_, index) => (
              <div
                key={index}
                class="border border-gray-200 rounded-lg p-6 animate-pulse"
              >
                <div class="h-40 bg-gray-100 rounded mb-4" />
                <div class="h-4 bg-gray-100 rounded w-3/4 mb-2" />
                <div class="h-4 bg-gray-100 rounded w-1/2" />
              </div>
            ))}
          </div>
        ) : props.events.length > 0 ? (
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6 auto-rows-[auto_auto_1fr_auto]">
            {props.events.slice(0, 4).map((event) => {
              const eventUrl = `/restaurant/${props.shopSlug}/reserve/events/${event.id}`;
              return (
                <article
                  key={event.id}
                  class="group grid grid-rows-[subgrid] row-span-4 border border-gray-200 rounded-lg overflow-hidden bg-white"
                >
                  <RouterLink
                    to={eventUrl}
                    class="bg-gray-100 aspect-[4/3] overflow-hidden"
                  >
                    <img
                      src={event.image || PLACEHOLDER_IMAGE}
                      alt={event.title}
                      class="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = PLACEHOLDER_IMAGE;
                      }}
                    />
                  </RouterLink>
                  <div class="px-5 pt-4">
                    <RouterLink to={eventUrl}>
                      <h3 class="font-serif text-xl font-light text-gray-900 group-hover:text-gray-700 transition-colors">
                        {event.title}
                      </h3>
                    </RouterLink>
                  </div>
                  <p class="px-5 text-sm text-gray-600 font-light line-clamp-3">
                    {event.description}
                  </p>
                  <div class="px-5 pb-5 pt-4 border-t border-gray-100">
                    <div class="flex items-center gap-4 text-gray-600">
                      <div class="flex items-center gap-1.5">
                        <span class="material-symbols-outlined text-sm">
                          calendar_today
                        </span>
                        <span class="text-sm font-light">
                          {formatDate(event.date)}
                        </span>
                      </div>
                      {event.time && (
                        <div class="flex items-center gap-1.5">
                          <span class="material-symbols-outlined text-sm">
                            schedule
                          </span>
                          <span class="text-sm font-light">{event.time}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        ) : (
          <div class="border border-dashed border-gray-200 rounded-lg p-8 text-center text-gray-500 font-light">
            {t("customer.restaurant_profile.no_upcoming_events")}
          </div>
        )}
      </section>
    );
  },
});
